'use client'

import { ChevronDown } from 'lucide-react'
import type { ReactElement, MouseEvent as ReactMouseEvent, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  FlagAT,
  FlagBE,
  FlagBG,
  FlagCY,
  FlagCZ,
  FlagDE,
  FlagDK,
  FlagEE,
  FlagEN,
  FlagES,
  FlagFI,
  FlagFR,
  FlagGR,
  FlagHR,
  FlagHU,
  FlagIE,
  FlagIT,
  FlagLT,
  FlagLU,
  FlagLV,
  FlagMT,
  FlagNL,
  FlagPL,
  FlagPT,
  FlagRO,
  FlagSE,
  FlagSI,
  FlagSK,
} from '../flags'

type SupportedLocale =
  | 'en' | 'fr' | 'es' | 'de' | 'de-AT' | 'fr-BE' | 'bg'
  | 'el-CY' | 'cs' | 'da' | 'et' | 'fi' | 'el' | 'hr'
  | 'hu' | 'ga' | 'it' | 'lt' | 'fr-LU' | 'lv' | 'mt'
  | 'nl' | 'pl' | 'pt' | 'ro' | 'sv' | 'sl' | 'sk'

type ClickOutsideHandler = (event: ReactMouseEvent) => void

function useOnClickOutside(
  dropdownRef: RefObject<HTMLDivElement>,
  onClickOutside: ClickOutsideHandler
): void {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)) {
        onClickOutside(event as unknown as ReactMouseEvent)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClickOutside, dropdownRef])
}

interface FlagProps {
  locale: SupportedLocale
}

const Flag = ({ locale }: FlagProps): ReactElement => {
  const FlagComponent = (): ReactElement => {
    switch (locale) {
      case 'en': return <FlagEN width={20} height={15} />
      case 'fr': return <FlagFR width={20} height={15} />
      case 'es': return <FlagES width={20} height={15} />
      case 'de': return <FlagDE width={20} height={15} />
      case 'de-AT': return <FlagAT width={20} height={15} />
      case 'fr-BE': return <FlagBE width={20} height={15} />
      case 'bg': return <FlagBG width={20} height={15} />
      case 'el-CY': return <FlagCY width={20} height={15} />
      case 'cs': return <FlagCZ width={20} height={15} />
      case 'da': return <FlagDK width={20} height={15} />
      case 'et': return <FlagEE width={20} height={15} />
      case 'fi': return <FlagFI width={20} height={15} />
      case 'el': return <FlagGR width={20} height={15} />
      case 'hr': return <FlagHR width={20} height={15} />
      case 'hu': return <FlagHU width={20} height={15} />
      case 'ga': return <FlagIE width={20} height={15} />
      case 'it': return <FlagIT width={20} height={15} />
      case 'lt': return <FlagLT width={20} height={15} />
      case 'fr-LU': return <FlagLU width={20} height={15} />
      case 'lv': return <FlagLV width={20} height={15} />
      case 'mt': return <FlagMT width={20} height={15} />
      case 'nl': return <FlagNL width={20} height={15} />
      case 'pl': return <FlagPL width={20} height={15} />
      case 'pt': return <FlagPT width={20} height={15} />
      case 'ro': return <FlagRO width={20} height={15} />
      case 'sv': return <FlagSE width={20} height={15} />
      case 'sl': return <FlagSI width={20} height={15} />
      case 'sk': return <FlagSK width={20} height={15} />
      default: {
        const _exhaustiveCheck: never = locale
        return _exhaustiveCheck
      }
    }
  }

  return FlagComponent()
}

interface LocaleSelectorProps {
  currentLocale: SupportedLocale
  localeOptions: readonly SupportedLocale[]
}

export default function LocaleSelector({
  currentLocale,
  localeOptions
}: LocaleSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>({} as HTMLDivElement)

  useOnClickOutside(dropdownRef, () => {
    setIsOpen(false)
  })

  const handleLocaleChange = (locale: SupportedLocale): void => {
    setIsOpen(false)
    document.cookie = `MINIMIST_LOCALE=${locale}; path=/; max-age=31536000`
    window.location.reload()
  }

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        className='flex items-center gap-2 text-sm text-gray-600 hover:text-black'
        onClick={() => { setIsOpen(!isOpen) }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select language"
      >
        <Flag locale={currentLocale} />
        <span>{currentLocale.toUpperCase()}</span>
        <ChevronDown className='h-4 w-4' />
      </button>

      {isOpen && (
        <div
          className='absolute right-0 bottom-full mb-2 w-32 rounded-md border bg-white shadow-lg z-10'
          role="listbox"
          aria-label="Select language"
        >
          {localeOptions.map((locale) => (
            <button
              key={locale}
              className='flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-100 text-left'
              onClick={() => { handleLocaleChange(locale) }}
              role="option"
              aria-selected={locale === currentLocale}
            >
              <Flag locale={locale} />
              <span>{locale.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
